import { EnvironmentCluster, MOMRegion, } from 'libs.react';
const APP_ENV_MAP = {
    [EnvironmentCluster.CN_DEV]: '-dev',
    [EnvironmentCluster.CN_ALI_PLATFORM]: '-platform',
    [EnvironmentCluster.CN_PROD]: '',
    [EnvironmentCluster.DEMO]: '-demo',
    [EnvironmentCluster.DEV]: '-dev',
    [EnvironmentCluster.EDGE]: '-edge',
    [EnvironmentCluster.EPHEMERAL]: '',
    [EnvironmentCluster.EU]: '-eu1',
    [EnvironmentCluster.EU_DEV]: '-eu1-dev',
    [EnvironmentCluster.INT_DEV]: '-int-dev',
    [EnvironmentCluster.LOCAL]: '-local',
    [EnvironmentCluster.PLATFORM]: '-platform',
    [EnvironmentCluster.PROD]: '',
    [EnvironmentCluster.QA]: '-qa',
    [EnvironmentCluster.TEST]: '-test',
};
/**
 * Whether a workflow or environment status means the study config is deployed. `amendment_failed`
 * counts: the deployment landed and only the post-deploy schedule update failed, so the environment
 * keeps the same TTL and time-travel clock as a `completed` one.
 */
const isDeployedStatus = (status) => {
    return status === 'completed' || status === 'amendment_failed';
};
const getEnvironmentStatus = (environment) => {
    const workflow = getEnvironmentWorkflow(environment);
    if (isDeployedStatus(workflow?.status) && hasExpired(environment)) {
        return 'expired';
    }
    if (workflow) {
        return workflow.status || 'active';
    }
    return environment.metadata.packages.length > 0 ? 'completed' : 'failed';
};
/**
 * Number of independent time-travel lanes for an env. 1 for single-lane / non-lane envs.
 */
const getLaneCount = (environment) => {
    const lanes = environment?.metadata?.lanes;
    return lanes && lanes > 1 ? lanes : 1;
};
/**
 * Rewrite a cortex base URL (https://api-<id>.<domain>) to a specific lane's host
 * (https://api-<id>-lN.<domain>). Lane 1 (or missing) returns the base URL unchanged.
 * Matches the in-pod haproxy lane routing (host suffix `-lN.`).
 */
const getLaneCortexUrl = (cortexURL, lane) => {
    if (!cortexURL || !lane || lane <= 1) {
        return cortexURL;
    }
    try {
        const url = new URL(cortexURL);
        const [first, ...rest] = url.hostname.split('.');
        url.hostname = [`${first}-l${lane}`, ...rest].join('.');
        return url.origin;
    }
    catch {
        return cortexURL;
    }
};
/**
 * Query param the new participant web app (SPA) reads to select a time-travel lane. It persists
 * the value and sends it back as the `X-Time-Travel-Lane` header; its backend applies the `-lN`
 * suffix to the cortex host. The SPA is served from a single shared host with the org code in the
 * path (e.g. https://study-latest.medable.com/GHBDP), so its own hostname must not be rewritten.
 */
const TIME_TRAVEL_LANE_PARAM = 'timeTravelLane';
/**
 * The legacy participant web app is deployed per environment under a `mystudy` host
 * (e.g. https://mystudy-m9778.alpha-latest.medable.com/?org=test-org, or `mystudy.qa` /
 * `mystudy.local`), and its lanes are reachable by suffixing that host with `-lN`. The new SPA is
 * served from a shared `study` host, so host suffixing does not resolve there.
 *
 * `get_study_url` returns either generation and the environment metadata carries no marker
 * distinguishing them (both register as the `patweb` webapp), so the host is the only signal.
 */
const LEGACY_PARTICIPANT_WEB_APP_HOST_LABEL = /^mystudy(-|$)/;
const isLegacyParticipantWebAppUrl = (url) => LEGACY_PARTICIPANT_WEB_APP_HOST_LABEL.test(url.hostname.split('.')[0] ?? '');
/**
 * Point a participant web app URL at a specific time-travel lane, using whichever mechanism the
 * deployed app generation supports. Missing lane returns the URL unchanged. The new SPA switches
 * lanes based on the *presence* of `timeTravelLane` rather than its value, so lane 1 must still
 * set the param explicitly there (otherwise a session already on a different lane never switches
 * back). The legacy app has no such ambiguity: lane 1 is simply the unsuffixed host.
 */
const getLaneUrl = (url, lane) => {
    if (!url || !lane) {
        return url;
    }
    try {
        const parsed = new URL(url);
        if (isLegacyParticipantWebAppUrl(parsed)) {
            if (lane > 1) {
                const [first, ...rest] = parsed.hostname.split('.');
                parsed.hostname = [`${first}-l${lane}`, ...rest].join('.');
            }
        }
        else {
            parsed.searchParams.set(TIME_TRAVEL_LANE_PARAM, String(lane));
        }
        return parsed.toString();
    }
    catch {
        return url;
    }
};
const buildEnvironmentUrl = ({ app = 'apps-dashboard', routePage = '', environment, lane, }) => {
    const iamServiceEndPoint = process.env.IAM_SERVICE_ENDPOINT;
    const cortexURL = getLaneCortexUrl(environment.cortexURL, lane);
    const buildSsoTokenUrl = (redirectUrl) => `${iamServiceEndPoint}/v1/sso/token?aud=${cortexURL}/${environment.code}&redirect_url=${encodeURIComponent(redirectUrl)}`;
    if (environment.deployment.cluster === EnvironmentCluster.EPHEMERAL) {
        const cortexWeb = cortexURL.replace('api', app);
        const redirectUrl = `${cortexWeb}/${routePage}?orgCode=${environment.code}`;
        return app === 'portal' && iamServiceEndPoint ? buildSsoTokenUrl(redirectUrl) : redirectUrl;
    }
    // If map doesn't include cluster, we default to the provided cluster as we consider production clusters covered
    const appEnv = APP_ENV_MAP[environment.deployment.cluster] ?? `-${environment.deployment.cluster}`;
    const domain = environment.deployment.region === MOMRegion.CN ? 'cn' : 'com';
    const redirectUrl = `https://${app}${appEnv}.medable.${domain}/${routePage}?orgCode=${environment.code}`;
    // Portal owns Study Manager login. Start through IAM SSO so the new tab carries the redirect context
    // instead of relying on an existing portal-* cookie and falling back to /login.
    if (iamServiceEndPoint && (app === 'portal' || environment.deployment.region === MOMRegion.CN)) {
        return buildSsoTokenUrl(redirectUrl);
    }
    return redirectUrl;
};
const getEnvironmentWorkflow = (environment) => {
    return environment.workflows[0];
};
const isUATEnvironment = (environment) => {
    return isTimeTravelEnvironment(environment) && environment.metadata.backupEnabled;
};
const isTimeTravelEnvironment = (environment) => {
    return environment.cluster === EnvironmentCluster.EPHEMERAL;
};
const hasExpired = (environment) => {
    if (isTimeTravelEnvironment(environment) && environment.metadata.expiresAt) {
        return environment.metadata.expiresAt < new Date().toISOString();
    }
    return false;
};
export const DeploymentUtils = {
    buildEnvironmentUrl,
    getEnvironmentWorkflow,
    getEnvironmentStatus,
    getLaneCount,
    getLaneCortexUrl,
    getLaneUrl,
    hasExpired,
    isDeployedStatus,
    isUATEnvironment,
    isTimeTravelEnvironment,
};
